import { StatusBar } from "@capacitor/status-bar";
import fetch from "cross-fetch";
import * as React from "react";
import { delay } from "../../../system/delay";

export function useCachedEqual<T>(value: T, equal: (a: T, b: T) => boolean): T {
  const ref = React.useRef<T | undefined>(undefined);

  if (ref.current !== undefined && (ref.current === value || equal(ref.current, value))) {
    return ref.current;
  } else {
    ref.current = value;
    return value;
  }
}

/**
 * Allows toggling into fullscreen mode (hides the phone status bar and some
 * other CSS changes).
 *
 * Will automatically switch out of fullscreen mode when the component is
 * destroyed.
 *
 * Note: Only one component should use this at a time. If multiple components
 * try to switch into fullscreen mode at the same time they will get in each
 * other's way.
 *
 * @returns a function that you should call `setFullscreenMode` that accepts a
 * single boolean parameter.
 */
export function useFullscreenModeToggle(): (fullscreen: boolean) => void {
  const isFullscreen = React.useRef(false);

  const setFullscreenMode = React.useCallback((fullscreen: boolean) => {
    if (fullscreen === isFullscreen.current) {
      return;
    }

    isFullscreen.current = fullscreen;

    if (!fullscreen) {
      StatusBar.show();
    } else {
      StatusBar.hide();
    }

    const elemToHide = document.querySelectorAll(
      ".VideoScreen__skeletonRoot > .ScreenSkeleton__header, " +
        ".VideoScreen__skeletonRoot > .ScreenSkeleton__chatButtonWrapper, " +
        ".VideoScreen__skeletonRoot > .BackButton__root"
    );
    elemToHide.forEach((elem) => {
      elem.classList.toggle("VideoScreen__hide");
    });
  }, []);

  React.useEffect(() => {
    // Register a cleanup handler:
    return () => {
      setFullscreenMode(false);
    };
  }, [setFullscreenMode]);

  return setFullscreenMode;
}

export function useCountdownTimer(fireFn: () => void): (millis: number | null) => void {
  const savedCallback = React.useRef(fireFn);
  const timeoutId = React.useRef<NodeJS.Timer | null>(null);

  React.useEffect(() => {
    savedCallback.current = fireFn;
  }, [fireFn]);

  const fireIn = React.useCallback((millis: number | null) => {
    if (timeoutId.current !== null) {
      clearTimeout(timeoutId.current);
    }

    if (millis !== null) {
      timeoutId.current = setTimeout(() => {
        savedCallback.current();
        timeoutId.current = null;
      }, millis);
    }
  }, []);

  React.useEffect(() => {
    return () => {
      if (timeoutId.current !== null) {
        clearTimeout(timeoutId.current);
      }
    };
  }, []);

  return fireIn;
}

export type ImageDimensionsUpdate =
  | ImageDimensionsUpdate.Loading
  | ImageDimensionsUpdate.Error
  | ImageDimensionsUpdate.NoImageUrl
  | ImageDimensionsUpdate.Dimensions;

export declare namespace ImageDimensionsUpdate {
  export interface Loading {
    type: "Loading";
  }

  /**
   * Unfortunately, there is no way in HTML5 to get the error reason (e.g.
   * Network error, Server error, not an image file)
   */
  export interface Error {
    type: "Error";
  }

  export interface NoImageUrl {
    type: "NoImageUrl";
  }

  export interface Dimensions {
    type: "Dimensions";
    width: number;
    height: number;
  }
}

/**
 * If there is an error retrieving the image dimensions, then the callback
 * will be called with `null`.
 *
 * If `imageUrl` is `null` then the `callback` will be called with `null`.
 */
export function useImageDimensions(
  imageUrl: string | null,
  callback: (dimensions: ImageDimensionsUpdate) => void
): void {
  const savedCallback = React.useRef(callback);

  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  React.useEffect(() => {
    const aliveToken = { alive: true };

    if (imageUrl === null) {
      savedCallback.current({
        type: "NoImageUrl",
      });
    } else {
      savedCallback.current({
        type: "Loading",
      });
      void getImageDimensions(imageUrl).then((result) => {
        if (!aliveToken.alive) {
          return;
        }

        if (result === null) {
          savedCallback.current({
            type: "Error",
          });
        } else {
          savedCallback.current({
            type: "Dimensions",
            width: result[0],
            height: result[1],
          });
        }
      });
    }

    return () => {
      aliveToken.alive = false;
    };
  }, [imageUrl]);
}

export function useInterval(callback: () => void, millis: number): void {
  const savedCallback = React.useRef<(() => void) | undefined>(undefined);

  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  React.useEffect(() => {
    const id = setInterval(() => {
      if (savedCallback.current !== undefined) {
        savedCallback.current();
      }
    }, millis);
    return () => clearInterval(id);
  }, [millis]);
}

export type FetchUpdate<T> =
  | FetchUpdate.Loading
  | FetchUpdate.Error
  | FetchUpdate.NoRequest
  | FetchUpdate.ResponseAvailable<T>;

export declare namespace FetchUpdate {
  export interface Loading {
    type: "Loading";
  }

  export interface Error {
    type: "Error";
    error: Error;
  }

  export interface NoRequest {
    type: "NoRequest";
  }

  export interface ResponseAvailable<T> {
    type: "ResponseAvailable";
    result: T;
  }
}

/**
 * @param parser should not perform any other side effects other than parsing
 * the response, because it may be called at unpredictable times
 */
export function useFetchResponse<T>(
  url: string | null,
  parser: (response: Response) => Promise<T>,
  callback: (update: FetchUpdate<T>) => void
): void {
  const savedCallback = React.useRef(callback);

  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  React.useEffect(() => {
    const aliveToken = { alive: true };

    if (url === null) {
      savedCallback.current({
        type: "NoRequest",
      });
    } else {
      savedCallback.current({
        type: "Loading",
      });
      void (async () => {
        let result: T;
        try {
          const response = await fetch(url);
          result = await parser(response);
        } catch (error) {
          if (!aliveToken.alive) {
            return;
          }
          savedCallback.current({
            type: "Error",
            error: error,
          });
          return;
        }
        if (!aliveToken.alive) {
          return;
        }
        savedCallback.current({
          type: "ResponseAvailable",
          result: result,
        });
      })();
    }

    return () => {
      aliveToken.alive = false;
    };
  }, [parser, url]);
}

async function getImageDimensions(imageUrl: string): Promise<[number, number] | null> {
  function currentTimeout(attempt: number): number {
    if (attempt < 2) {
      return 2000;
    } else if (attempt < 4) {
      return 5000;
    } else {
      return 20000;
    }
  }

  const NUM_ATTEMPTS = 8;
  for (let i = 0; i < NUM_ATTEMPTS; ++i) {
    const result = await getImageDimensionsWithTimeout(imageUrl, currentTimeout(i));
    if (result !== null) {
      return result;
    }

    if (i < NUM_ATTEMPTS - 1) {
      console.warn("WARNING: failed to load image (will retry...)", imageUrl);
      await delay(1000);
    }
  }

  return null;
}

async function getImageDimensionsWithTimeout(
  imageUrl: string,
  timeout: number
): Promise<[number, number] | null> {
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      resolve(null);
    }, timeout);

    const img = document.createElement("img");
    img.onload = () => {
      clearTimeout(timeoutId);
      resolve([img.naturalWidth, img.naturalHeight]);
    };
    img.src = imageUrl;
    img.onerror = () => {
      clearTimeout(timeoutId);
      resolve(null);
    };
  });
}

/**
 * Returns a unique-ish 52 bit number (based on timestamp and random number)
 */
export function generateClientUniqueId(): number {
  // We take a 42 bit epoch and 10 bit random number

  const buf = new Uint8Array(2);
  window.crypto.getRandomValues(buf);

  const random = (buf[0] & 3) * 256 + buf[1]; // tslint:disable-line:no-bitwise

  // Assumed to be 42 bit:
  const epoch = new Date().getTime();

  return epoch * 1024 + random;
}
